import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowRight, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { ApexLogo } from "@/components/ApexLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");

  useEffect(() => { if (user) navigate("/admin", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfa_required) { setMfaToken(res.mfa_token); }
      else navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await verifyMfa(mfaToken, code);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Verification failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-5" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #142234, #07111E)" }}>
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><ApexLogo light /></div>
        <div className="rounded-2xl border border-navy-light bg-navy-surface p-8 shadow-2xl">
          {!mfaToken ? (
            <>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/15 text-gold"><Lock size={18} /></div>
                <div>
                  <h1 className="font-serif text-2xl font-semibold text-white">Admin Sign In</h1>
                  <p className="text-xs text-slate-400">Private ApexPath administration area</p>
                </div>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input id="email" type="email" data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" placeholder="you@apexpath.co.uk" required />
                </div>
                <div>
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <div className="relative mt-1.5">
                    <Input id="password" type={showPw ? "text" : "password"} data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                      className="bg-white text-slate-900 border-slate-300 placeholder:text-slate-400 pr-10" placeholder="••••••••" required />
                    <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="login-password-toggle"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-700" aria-label={showPw ? "Hide password" : "Show password"}>
                      {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="admin-login-submit-btn" className="btn-gold w-full disabled:opacity-60">
                  {loading ? "Signing in…" : "Sign In"} <ArrowRight size={16} />
                </button>
              </form>
              <div className="mt-4 flex items-center justify-between text-xs">
                <Link to="/forgot-password" className="text-slate-400 hover:text-gold">Forgot password?</Link>
                <Link to="/" className="text-slate-400 hover:text-gold">← Back to website</Link>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/15 text-gold"><ShieldCheck size={18} /></div>
                <div>
                  <h1 className="font-serif text-2xl font-semibold text-white">Two-step verification</h1>
                  <p className="text-xs text-slate-400">Enter the 6-digit code from your authenticator app</p>
                </div>
              </div>
              <form onSubmit={submitMfa} className="space-y-4">
                <div>
                  <Label htmlFor="code" className="text-slate-300">Authentication code</Label>
                  <Input id="code" inputMode="numeric" data-testid="admin-mfa-input" value={code} onChange={(e) => setCode(e.target.value)}
                    className="mt-1.5 bg-white text-slate-900 border-slate-300 tracking-[0.4em] text-center text-lg" placeholder="123456" autoFocus required />
                  <p className="mt-2 text-xs text-slate-500">You can also enter one of your recovery codes.</p>
                </div>
                {error && <p className="text-sm text-red-400" data-testid="admin-mfa-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="admin-mfa-submit-btn" className="btn-gold w-full disabled:opacity-60">
                  {loading ? "Verifying…" : "Verify"} <ArrowRight size={16} />
                </button>
                <button type="button" onClick={() => { setMfaToken(""); setCode(""); setError(""); }} className="w-full text-xs text-slate-400 hover:text-gold">← Back to sign in</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
